// src/components/admin/payroll/components/VirtualizedEmployeeList.js
// Virtualized employee list for efficient rendering of 1000+ employees

import React, { useCallback, useMemo } from 'react';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import {
    CheckSquare, Square, CheckCircle, AlertCircle, XCircle,
    Eye, ChevronRight, Calendar, User
} from 'lucide-react';
import { useTheme } from '../../../../contexts/ThemeContext';

const ROW_HEIGHT = 72;

const VirtualizedEmployeeList = ({
    employees = [],
    selectedIds = [],
    onSelectEmployee,
    onViewEmployee,
    onSelectAll,
    loading = false
}) => {
    const { theme } = useTheme();

    // Ensure employees is always an array
    const safeEmployees = Array.isArray(employees) ? employees : [];

    // Get status badge
    const getStatusBadge = useCallback((validation) => {
        if (!validation) return null;

        const config = {
            auto_validated: {
                icon: CheckCircle,
                label: 'Auto-Validated',
                bgLight: 'bg-green-100',
                bgDark: 'bg-green-900/30',
                textLight: 'text-green-700',
                textDark: 'text-green-400'
            },
            needs_review: {
                icon: AlertCircle,
                label: 'Needs Review',
                bgLight: 'bg-yellow-100',
                bgDark: 'bg-yellow-900/30',
                textLight: 'text-yellow-700',
                textDark: 'text-yellow-400'
            },
            has_issues: {
                icon: XCircle,
                label: 'Has Issues',
                bgLight: 'bg-red-100',
                bgDark: 'bg-red-900/30',
                textLight: 'text-red-700',
                textDark: 'text-red-400'
            }
        };

        const statusConfig = config[validation.status] || config.needs_review;
        const Icon = statusConfig.icon;

        return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                theme === 'dark'
                    ? `${statusConfig.bgDark} ${statusConfig.textDark}`
                    : `${statusConfig.bgLight} ${statusConfig.textLight}`
            }`}>
                <Icon className="w-3 h-3" />
                {statusConfig.label}
            </span>
        );
    }, [theme]);

    // Get validation flags summary
    const getFlagsSummary = useCallback((validation) => {
        if (!validation?.flags || validation.flags.length === 0) return null;

        const flagLabels = validation.flags.map(f => {
            if (f.category === 'new_joiner') return 'New Joiner';
            if (f.category === 'exit') return 'Exiting';
            if (f.category === 'lop') return `${f.lopDays} LOP`;
            if (f.category === 'partial_days') return `${f.partialDays} Partial`;
            return f.category;
        });

        return flagLabels.slice(0, 2).join(', ') + (flagLabels.length > 2 ? ` +${flagLabels.length - 2}` : '');
    }, []);

    // Row renderer - use closure to access data (avoiding itemData issues)
    const Row = useCallback(({ index, style }) => {
        const employee = safeEmployees[index];
        if (!employee) return null;

        const isSelected = selectedIds.includes(employee.id);
        const validation = employee.validation;

        return (
            <div
                style={style}
                className={`flex items-center px-4 border-b transition-colors ${
                    theme === 'dark'
                        ? `border-gray-700 ${isSelected ? 'bg-blue-900/20' : 'hover:bg-gray-700/50'}`
                        : `border-gray-100 ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`
                }`}
            >
                {/* Checkbox */}
                <div className="flex-shrink-0 pr-3">
                    <button
                        onClick={() => onSelectEmployee?.(employee.id)}
                        className={`p-1 rounded ${
                            theme === 'dark' ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                        }`}
                    >
                        {isSelected ? (
                            <CheckSquare className="w-5 h-5 text-blue-500" />
                        ) : (
                            <Square className={`w-5 h-5 ${
                                theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                            }`} />
                        )}
                    </button>
                </div>

                {/* Employee Info */}
                <div className="flex-1 min-w-0 pr-4">
                    <div className="flex items-center gap-2">
                        <span className={`font-medium truncate ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                            {employee.first_name} {employee.last_name}
                        </span>
                        <span className={`text-xs ${
                            theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                            {employee.employee_code}
                        </span>
                    </div>
                    <div className={`flex items-center gap-3 text-xs mt-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                        <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {employee.department || 'No Dept'}
                        </span>
                        <span>{employee.designation || 'No Role'}</span>
                    </div>
                </div>

                {/* Attendance Info */}
                <div className={`flex-shrink-0 w-32 text-center ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                    <div className="text-sm font-medium">
                        {employee.present_days || 0} / {employee.attendance_records || 0}
                    </div>
                    <div className={`text-xs ${
                        theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                        {employee.lop_days > 0 && (
                            <span className="text-red-500">{employee.lop_days} LOP</span>
                        )}
                        {employee.leave_days > 0 && (
                            <span className="ml-1">{employee.leave_days} Leave</span>
                        )}
                        {!employee.lop_days && !employee.leave_days && 'Present'}
                    </div>
                </div>

                {/* Salary Info */}
                <div className={`flex-shrink-0 w-28 text-right ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                    <div className="text-sm font-medium">
                        ₹{((employee.ctc || employee.annual_ctc || employee.pay_package?.ctc || employee.pay_package?.annual_ctc || 0) / 12 / 1000).toFixed(0)}K
                    </div>
                    <div className={`text-xs ${
                        theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                        /month
                    </div>
                </div>

                {/* Validation Status */}
                <div className="flex-shrink-0 w-36 text-center">
                    {getStatusBadge(validation)}
                    {validation?.flags?.length > 0 && (
                        <div className={`text-xs mt-1 truncate ${
                            theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                            {getFlagsSummary(validation)}
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="flex-shrink-0 pl-4">
                    <button
                        onClick={() => onViewEmployee?.(employee)}
                        className={`p-2 rounded-lg ${
                            theme === 'dark'
                                ? 'hover:bg-gray-600 text-gray-400'
                                : 'hover:bg-gray-200 text-gray-500'
                        }`}
                        title="View details"
                    >
                        <Eye className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    }, [safeEmployees, selectedIds, theme, onSelectEmployee, onViewEmployee, getStatusBadge, getFlagsSummary]);

    // Header
    const Header = useMemo(() => (
        <div className={`flex items-center px-4 py-3 border-b font-medium text-xs uppercase tracking-wider ${
            theme === 'dark'
                ? 'bg-gray-800 border-gray-700 text-gray-400'
                : 'bg-gray-50 border-gray-200 text-gray-500'
        }`}>
            <div className="flex-shrink-0 pr-3 w-8">
                <button
                    onClick={onSelectAll}
                    className={`p-1 rounded ${
                        theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
                    }`}
                >
                    {selectedIds.length === safeEmployees.length && safeEmployees.length > 0 ? (
                        <CheckSquare className="w-4 h-4 text-blue-500" />
                    ) : selectedIds.length > 0 ? (
                        <div className="relative">
                            <Square className="w-4 h-4" />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-2 h-0.5 bg-current" />
                            </div>
                        </div>
                    ) : (
                        <Square className="w-4 h-4" />
                    )}
                </button>
            </div>
            <div className="flex-1">Employee</div>
            <div className="flex-shrink-0 w-32 text-center">Attendance</div>
            <div className="flex-shrink-0 w-28 text-right">Salary</div>
            <div className="flex-shrink-0 w-36 text-center">Status</div>
            <div className="flex-shrink-0 w-12"></div>
        </div>
    ), [theme, selectedIds.length, safeEmployees.length, onSelectAll]);

    // Note: We're not using itemData prop as it can cause issues with react-window v2
    // Instead, the Row component accesses data through closure

    // Loading skeleton
    if (loading && safeEmployees.length === 0) {
        return (
            <div className={`rounded-lg border overflow-hidden ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                {Header}
                <div className="p-4">
                    {[1, 2, 3, 4, 5].map(i => (
                        <div key={i} className="animate-pulse flex items-center py-3">
                            <div className="w-5 h-5 bg-gray-300 rounded mr-4" />
                            <div className="flex-1">
                                <div className="h-4 bg-gray-300 rounded w-1/3 mb-2" />
                                <div className="h-3 bg-gray-200 rounded w-1/4" />
                            </div>
                            <div className="w-24 h-4 bg-gray-300 rounded mx-4" />
                            <div className="w-20 h-4 bg-gray-300 rounded mx-4" />
                            <div className="w-24 h-6 bg-gray-300 rounded mx-4" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Empty state
    if (safeEmployees.length === 0) {
        return (
            <div className={`rounded-lg border overflow-hidden ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                {Header}
                <div className="p-8 text-center">
                    <User className={`w-12 h-12 mx-auto mb-3 ${
                        theme === 'dark' ? 'text-gray-600' : 'text-gray-300'
                    }`} />
                    <p className={`font-medium ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                        No employees found
                    </p>
                    <p className={`text-sm mt-1 ${
                        theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                        Try adjusting your filters
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={`rounded-lg border overflow-hidden ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
            {Header}
            <div style={{ height: Math.min(safeEmployees.length * ROW_HEIGHT, 500) }}>
                <AutoSizer>
                    {({ height, width }) => {
                        // Guard against invalid dimensions
                        if (!height || !width || height <= 0 || width <= 0) {
                            return null;
                        }
                        return (
                            <List
                                height={height}
                                width={width}
                                itemCount={safeEmployees.length}
                                itemSize={ROW_HEIGHT}
                                overscanCount={5}
                            >
                                {Row}
                            </List>
                        );
                    }}
                </AutoSizer>
            </div>
        </div>
    );
};

export default VirtualizedEmployeeList;
